import { useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// SEO translations for all pages
const seoData = {
  en: {
    title: 'PrimeDigital - Web Development, AI Automation & Digital Marketing | 5-Day Delivery',
    description: 'Premium digital agency for startups and SMBs. Custom websites, AI chatbots, n8n workflow automation, and digital marketing. Fast delivery in 5 days. Free consultation!',
    keywords: 'web development spain, AI chatbots, n8n automation, make automation, workflow automation, digital marketing agency, ecommerce development, SEO services, lead generation AI, custom web apps, startup web design',
    ogTitle: 'PrimeDigital - Web & AI Automation Solutions | 5-Day Delivery',
    ogDescription: 'Transform your business with AI-powered web solutions. Custom websites, intelligent chatbots, workflow automation with n8n/Make, and digital marketing. Guaranteed 5-day delivery.',
  },
  es: {
    title: 'PrimeDigital - Desarrollo Web, Automatización IA y Marketing Digital | Entrega en 5 Días',
    description: 'Agencia digital premium para startups y pymes. Sitios web personalizados, chatbots IA, automatización con n8n/Make, y marketing digital. Entrega rápida en 5 días. ¡Consulta gratis!',
    keywords: 'desarrollo web españa, chatbots IA, automatización n8n, automatización make, automatización de flujos de trabajo, agencia marketing digital, desarrollo ecommerce, servicios SEO, generación leads IA, aplicaciones web, diseño web startups',
    ogTitle: 'PrimeDigital - Soluciones Web y Automatización IA | Entrega en 5 Días',
    ogDescription: 'Transforma tu negocio con soluciones web impulsadas por IA. Sitios web personalizados, chatbots inteligentes, automatización con n8n/Make, y marketing digital. Entrega garantizada en 5 días.',
  },
  de: {
    title: 'PrimeDigital - Webentwicklung, KI-Automatisierung & Digitales Marketing | 5-Tage Lieferung',
    description: 'Premium-Digitalagentur für Startups und KMU. Individuelle Websites, KI-Chatbots, n8n Workflow-Automatisierung und digitales Marketing. Schnelle Lieferung in 5 Tagen. Kostenlose Beratung!',
    keywords: 'webentwicklung spanien, KI chatbots, n8n automatisierung, make automatisierung, workflow automatisierung, digital marketing agentur, ecommerce entwicklung, SEO dienstleistungen, KI lead generierung, web apps, startup webdesign',
    ogTitle: 'PrimeDigital - Web & KI-Automatisierung Lösungen | 5-Tage Lieferung',
    ogDescription: 'Transformieren Sie Ihr Unternehmen mit KI-gestützten Weblösungen. Individuelle Websites, intelligente Chatbots, Workflow-Automatisierung mit n8n/Make und digitales Marketing. Garantierte 5-Tage-Lieferung.',
  }
};

// Hreflang configurations
const hreflangConfig = {
  es: 'https://primedigital.es',
  en: 'https://primedigital.es/en',
  de: 'https://primedigital.es/de',
  'x-default': 'https://primedigital.es'
};

const SEOHead = () => {
  const { language } = useLanguage();
  const seo = seoData[language] || seoData.es;

  const updateMetaTag = useCallback((name, content, attribute = 'name') => {
    let element = document.querySelector(`meta[${attribute}="${name}"]`);
    if (element) {
      element.setAttribute('content', content);
    } else {
      element = document.createElement('meta');
      element.setAttribute(attribute, name);
      element.setAttribute('content', content);
      document.head.appendChild(element);
    }
  }, []);

  const updateHreflangLinks = useCallback(() => {
    // Remove existing hreflang links
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

    // Add new hreflang links
    Object.entries(hreflangConfig).forEach(([lang, url]) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = url;
      document.head.appendChild(link);
    });
  }, []);

  const updateCanonical = useCallback((lang) => {
    let canonical = document.querySelector('link[rel="canonical"]');
    const baseUrl = 'https://primedigital.es';
    const url = lang === 'es' ? baseUrl : `${baseUrl}/${lang}`;
    
    if (canonical) {
      canonical.href = url;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }
  }, []);

  useEffect(() => {
    // Update document title
    document.title = seo.title;

    // Update meta description
    updateMetaTag('description', seo.description);
    updateMetaTag('keywords', seo.keywords);

    // Update language
    document.documentElement.lang = language;
    updateMetaTag('language', language === 'es' ? 'Spanish' : language === 'en' ? 'English' : 'German');

    // Update Open Graph tags
    updateMetaTag('og:title', seo.ogTitle, 'property');
    updateMetaTag('og:description', seo.ogDescription, 'property');
    updateMetaTag('og:locale', language === 'es' ? 'es_ES' : language === 'en' ? 'en_US' : 'de_DE', 'property');

    // Update Twitter tags
    updateMetaTag('twitter:title', seo.ogTitle);
    updateMetaTag('twitter:description', seo.ogDescription);

    // Update hreflang links
    updateHreflangLinks();

    // Update canonical
    updateCanonical(language);

  }, [language, seo, updateMetaTag, updateHreflangLinks, updateCanonical]);

  return null; // This component doesn't render anything
};

export default SEOHead;
